import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/services/auth/auth.service';
import { Company } from 'app/services/company/company.model';
import { CompanyService } from 'app/services/company/company.service';
import { GetInformationForTheAuthoritiesResponse, ManagementService } from 'app/services/management.service';
import { ProService } from 'app/services/pro/pro.service';
import { VehiclesService } from 'app/services/vehicles/vehicles.service';
import { DriversService } from 'app/services/drivers/drivers.service';
import { MessageService } from 'primeng/api';
import { NameFormatService } from 'app/services/name-format/name-format.service';

@Component({
  selector: 'app-management',
  templateUrl: './management.component.html',
  styleUrls: ['./management.component.css']
})
export class ManagementComponent implements OnInit {

  rangeDates: Date[] = [new Date(), new Date()];
  rangeDatesAuthorities: Date[] | null = null;

  fileTypes: {
    name: string;
    value: number;
  }[] = [
      { name: 'Driver', value: 2 },
      { name: 'Vehicle', value: 1 },
    ];

  selectedFileTypes: { name: string, value: number }[] = [...this.fileTypes];

  companies: Company[] = [];
  selectedCompanies: Company[] = [];
  progress: number | null = null;

  constructor(
    private readonly translate: TranslateService,
    private readonly companyService: CompanyService,
    private readonly managementService: ManagementService,
    private readonly authService: AuthService,
    private readonly vehicleService: VehiclesService,
    private readonly proService: ProService,
    private readonly driverService: DriversService,
    private readonly messageService: MessageService,
    private readonly nameFormatService: NameFormatService,
  ) { }

  vehicles: {
    vin: string;
    registrationNumber: string;
  }[] = [];

  selectedVehicles: {
    vin: string;
    registrationNumber: string;
  }[] = [];

  authoritiesData: GetInformationForTheAuthoritiesResponse;
  authoritiesLoading = false;
  dataTableRows = []
  analysisStatus = null;
  missingDrivers = [];
  isPro = false;

  // New section for download by driver and vehicle
  drivers: any[] = [];
  selectedDrivers: any[] = [];
  selectedVehiclesForDownload: any[] = [];
  rangeDatesPerEntity: Date[] | null = null;
  perEntityProgress: number | null = null;
  tcFormatOptions = ['', 'DDD', 'C1B', 'TGD'];
  dcFormatOptions = ['', 'DDD', 'C1B', 'TGD'];
  selectedTcFormat = '';
  selectedDcFormat = '';
  selectedCompanyForEntities: Company | null = null;
  driversLoading = false;
  vehiclesLoading = false;

  ngOnInit(): void {

    this.proService.pro$.subscribe((isPro) => {
      this.isPro = isPro.pro !== 'NOT_PRO';
    })

    this.companyService.getAllSubcompanies().subscribe((response) => {
      this.companies = response.sort((a, b) => {
        const x = a.name.toLowerCase();
        const y = b.name.toLowerCase();
        if (x < y) {
          return -1;
        }
        if (x > y) {
          return 1;
        }
        return 0;
      })
    })

    this.authService.currentCompanyId.subscribe((companyId) => {
      this.analysisStatus = null;
      this.authoritiesLoading = false;
      this.selectedVehicles = [];
      this.dataTableRows = [];
      this.missingDrivers = [];
      this.vehicleService.getAllVehiclesInVehicleReport(companyId).subscribe((response) => {
        this.vehicles = response.Records.filter((vehicle) => vehicle.status === 'ACTIVE').map((vehicle) => {
          return {
            vin: vehicle.identificationNumber,
            registrationNumber: vehicle.registrationNumber
          }
        })
      })
    })
  }

  onCompanyChangeForEntities() {
    if (!this.selectedCompanyForEntities) {
      this.drivers = [];
      this.selectedDrivers = [];
      this.selectedVehiclesForDownload = [];
      return;
    }

    this.selectedDrivers = [];
    this.selectedVehiclesForDownload = [];
    this.driversLoading = true;
    this.vehiclesLoading = true;

    this.vehicleService.getAllVehiclesInVehicleReport(this.selectedCompanyForEntities.id).subscribe({
      next: (response) => {
        this.vehicles = response.Records.filter((vehicle) => vehicle.status === 'ACTIVE').map((vehicle) => {
          return {
            vin: vehicle.identificationNumber,
            registrationNumber: vehicle.registrationNumber
          }
        });
        this.vehiclesLoading = false;
      },
      error: () => {
        this.vehiclesLoading = false;
      }
    });

    this.driverService.getAllDrivers(this.selectedCompanyForEntities.id).subscribe({
      next: (response) => {
        this.drivers = response.map((driver) => {
          return {
            cardNumber: driver.driver.cardNumber,
            name: this.nameFormatService.formatName(driver.driver.driverFirstNames, driver.driver.driverSurnames)
          }
        });
        this.driversLoading = false;
      },
      error: () => {
        this.driversLoading = false;
      }
    });
  }

  async exportAllFiles() {

    if (this.selectedCompanies.length === 0) {
      return;
    }
    if (this.selectedFileTypes.length === 0) {
      this.selectedFileTypes = [...this.fileTypes];
    }

    const selectedCompanies = this.selectedCompanies;
    const selectedFileTypes = this.selectedFileTypes.map(fileType => fileType.value);
    const selectedPeriod = [...this.rangeDates];

    this.progress = 0.001;
    for (let i = 0; i < selectedCompanies.length; i++) {
      const company = selectedCompanies[i];

      await new Promise((resolve) => { setTimeout(resolve, 1000 * 5) });
      for (const fileType of selectedFileTypes) {
        try {
          const response = await this.companyService.downloadFilesPerClient(company.id, fileType as 1 | 2, Math.floor(selectedPeriod[0].getTime() / 1000), Math.floor(selectedPeriod[1].getTime() / 1000 + 24 * 60 * 60)).toPromise();
          if (response.type !== 'application/octet-stream' || response.size < 200) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('No files'),
              detail: this.translate.instant('No files found in the selected period'),
              life: 5000
            });
            continue;
          }
          const blob = new Blob([response], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          //date = YYYYMMDD
          const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
          a.href = url;
          a.download = `${company.name}_${date}_${fileType == 2 ? 'Driver' : 'Vehicle'}_Files.zip`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
        } catch (e) {
          console.error(e);
        }
      }
      this.progress = Math.floor((i + 1) / selectedCompanies.length * 100);
    }
    console.log('done');
    this.progress = 100;
    setTimeout(() => {
      this.reset();
    }, 2000);
  }

  async downloadFilesPerEntities() {
    if (!this.rangeDatesPerEntity || this.rangeDatesPerEntity.length === 0) {
      return;
    }
    if (this.selectedDrivers.length === 0 && this.selectedVehiclesForDownload.length === 0) {
      return;
    }
    if (!this.selectedCompanyForEntities) {
      return;
    }

    this.perEntityProgress = 0.001;
    const companyName = this.selectedCompanyForEntities.name;
    const periodStart = Math.floor(this.rangeDatesPerEntity[0].getTime() / 1000);
    const periodEnd = Math.floor(this.rangeDatesPerEntity[1].getTime() / 1000 + 24 * 60 * 60);

    try {
      const response = await this.managementService.downloadFilesPerEntities(
        this.selectedVehiclesForDownload.map(v => v.vin),
        this.selectedDrivers.map(d => d.cardNumber),
        this.selectedCompanyForEntities.id,
        periodStart,
        periodEnd,
        this.selectedTcFormat,
        this.selectedDcFormat
      ).toPromise();

      if (!response) {
        this.perEntityProgress = null;
        return;
      }

      const blob = new Blob([response], { type: 'application/octet-stream' });
      if(blob.size < 200) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('No files'),
          detail: this.translate.instant('No files found in the selected period'),
          life: 5000
        });
        this.perEntityProgress = null;
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const startDateStr = this.rangeDatesPerEntity[0].toISOString().split('T')[0];
      const endDateStr = this.rangeDatesPerEntity[1].toISOString().split('T')[0];
      a.href = url;
      a.download = `${companyName}_${startDateStr}_${endDateStr}_Files.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      this.perEntityProgress = 100;
      setTimeout(() => {
        this.perEntityProgress = null;
      }, 2000);
    } catch (e) {
      console.error(e);
      this.perEntityProgress = null;
    }
  }

  async downloadFilesForAuthorities() {
    const companyName = this.companies.find(company => company.id === this.authService.currentCompanyIdValue())?.name;
    const periodStart = new Date(this.rangeDatesAuthorities[0]);
    periodStart.setDate(periodStart.getDate() + 1);
    const periodEnd = new Date(this.rangeDatesAuthorities[1]);
    periodEnd.setDate(periodEnd.getDate() + 1);
    try {
      const response = await this.companyService.downloadFilesForTheAuthorities(this.authService.currentCompanyIdValue(), Math.floor(this.rangeDatesAuthorities[0].getTime() / 1000), Math.floor(this.rangeDatesAuthorities[1].getTime() / 1000 + 24 * 60 * 60), this.selectedVehicles.map(v => v.vin)).toPromise();

      if (response.type !== 'application/octet-stream' || response.size < 200) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('No files'),
          detail: this.translate.instant('No files found in the selected period'),
          life: 5000
        });
        return;
      }
      const blob = new Blob([response], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      //date = YYYYMMDD
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      a.href = url;
      a.download = `${companyName}_${periodStart.toISOString().split('T')[0]}_${periodEnd.toISOString().split('T')[0]}_Files.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      console.error(e);
    }
  }



  checkFilesForAuthorities() {
    this.authoritiesLoading = true;
    this.analysisStatus = null;
    this.dataTableRows = [];
    this.missingDrivers = [];
    let allDrivers = []
    let missingDrivers = [];
    this.managementService.getInformationForTheAuthorities(
      this.authService.currentCompanyIdValue(),
      this.rangeDatesAuthorities[0],
      this.rangeDatesAuthorities[1],
      this.selectedVehicles.map((vehicle) => vehicle.vin),
    ).subscribe((response) => {
      if (response) {
        this.authoritiesData = response;
        for (const analysis of this.authoritiesData.coverageAnalysis) {
          const vehicleCoverage = Object.values(analysis.coverageMap).filter(e => e.type === 'vehicle')[0];
          if (!vehicleCoverage) {
            const periodStart = new Date(this.rangeDatesAuthorities[0]);
            periodStart.setDate(periodStart.getDate() + 1);
            const periodEnd = new Date(this.rangeDatesAuthorities[1]);
            periodEnd.setDate(periodEnd.getDate() + 1);
            this.dataTableRows.push({
              id: analysis.selectedVehicle + analysis.selectedVehicleRegNr,
              type: 'vehicleHeader',
              vin: analysis.selectedVehicle,
              registrationNumber: analysis.selectedVehicleRegNr,
              status: 'missing',
              missingPeriods: [{
                start: periodStart.toISOString().split('T')[0],
                end: periodEnd.toISOString().split('T')[0],
              }],
              driverStatus: 'missing',
            })
            this.analysisStatus = 'missing';
            continue;
          }
          const daysCoverage = [...vehicleCoverage.covered.map((day) => ({ day: day, vehicleStatus: 'covered', driverStatus: 'covered', missingDrivers: [] })), ...vehicleCoverage.missing.map((day) => ({ day: day, vehicleStatus: 'missing', driverStatus: 'covered', missingDrivers: [] }))]
          const driverCoverages = Object.entries(analysis.coverageMap).filter(e => e[1].type === 'driver');
          allDrivers.push(...driverCoverages.map(e => e[0]));
          for (const driverCoverage of driverCoverages) {
            for (const missingCoverage of driverCoverage[1].missing) {
              const day = daysCoverage.find(e => e.day === missingCoverage);
              if (day) {
                day.driverStatus = 'missing';
                day.missingDrivers.push(driverCoverage[0]);
                missingDrivers.push(driverCoverage[0]);
              }
            }
          }
          if (analysis.notUploadedDriverCards.length > 0) {
            for (const dayCoverage of daysCoverage) {
              dayCoverage.driverStatus = 'missing'
              dayCoverage.missingDrivers.push(...analysis.notUploadedDriverCards);
              allDrivers.push(...analysis.notUploadedDriverCards);
              missingDrivers.push(...analysis.notUploadedDriverCards);

            }
          }

          const sortedDates = vehicleCoverage.missing.sort();
          const missingPeriods = sortedDates.reduce((acc, dateStr, index, arr) => {
            if (index === 0) {
              acc.push({ start: dateStr, end: dateStr });
              return acc;
            }

            const prevDateStr = arr[index - 1];
            const prevDate = new Date(prevDateStr);
            const currDate = new Date(dateStr);
            const prevDatePlusOne = new Date(prevDate);
            prevDatePlusOne.setDate(prevDate.getDate() + 1);
            if (currDate.getTime() - prevDatePlusOne.getTime()<= 65*60*1000) {
              acc[acc.length - 1].end = dateStr;
            } else {
              acc.push({ start: dateStr, end: dateStr });
            }
            return acc;

          }, []);

          const vehicleAnalysis = {
            id: analysis.selectedVehicle + analysis.selectedVehicleRegNr,
            type: 'vehicleHeader',
            vin: analysis.selectedVehicle,
            registrationNumber: analysis.selectedVehicleRegNr,
            status: 'covered',
            missingPeriods: missingPeriods,
            driverStatus: daysCoverage.some(e => e.driverStatus === 'missing') ? 'missing' : 'covered',
          }
          //if any of days is missing, the vehicle is missing
          if (daysCoverage.some(e => e.vehicleStatus === 'missing') || daysCoverage.some(e => e.driverStatus === 'missing')) {
            vehicleAnalysis.status = 'missing';
            vehicleAnalysis.driverStatus = 'missing';
            this.analysisStatus = 'missing';
          }
          else {
            vehicleAnalysis.status = 'covered';
          }
          this.dataTableRows.push(vehicleAnalysis)

          // get all periods that are missing from the vehicleCoverage, where vehicleStatus is missing


          // missingPeriods.forEach((period) => {
          //   this.dataTableRows.push({
          //     id: analysis.selectedVehicle + analysis.selectedVehicleRegNr,
          //     type: 'vehiclePeriodMissing',
          //     startDate: period.start,
          //     endDate: period.end,
          //   })
          // })

          //unique missing drivers
          const allMissingDrivers = daysCoverage.filter(e => e.missingDrivers.length > 0).map(e => e.missingDrivers).flat();
          const uniqueMissingDrivers = [...new Set(allMissingDrivers)];
          uniqueMissingDrivers.forEach((driver) => {
            this.dataTableRows.push({
              id: analysis.selectedVehicle + analysis.selectedVehicleRegNr,
              type: 'vehicleDriverMissing',
              driverName: driver,
            })
          })
          // missingDrivers = [...new Set([...this.missingDrivers, ...uniqueMissingDrivers])];

          // daysCoverage.map((day) => {
          //   this.dataTableRows.push({
          //     id: analysis.selectedVehicle + analysis.selectedVehicleRegNr,
          //     type: 'vehicleDay',
          //     vehicleStatus: day.vehicleStatus,
          //     driverStatus: day.driverStatus,
          //     day: day.day,
          //     missingDrivers: day.missingDrivers,
          //   })
          // })

        }
      }
      if (this.analysisStatus === null) {
        this.analysisStatus = 'covered';
      }
      this.authoritiesLoading = false;
      allDrivers = [...new Set(allDrivers)];
      const sortedDrivers = allDrivers.sort();
      const missingDriversMap = new Map<string, boolean>();
      missingDrivers.forEach((driver) => {
        missingDriversMap.set(driver, true);
      })
      this.missingDrivers = sortedDrivers.map((driver) => {
        return {
          name: driver,
          status: missingDriversMap.get(driver) ? 'missing' : 'covered',
        }
      })
    });
  }

  // isAuthoritiesResponseComplete() {
  //   this.authoritiesResponse.coverageAnalysis.map((coverage) => {
  //     if(coverage.notUploadedDriverCards.length > 0) return false;


  //   })
  // }


  reset() {
    this.selectedCompanies = [];
    this.selectedFileTypes = [...this.fileTypes];
    // this.rangeDates = [new Date(), new Date()];
    this.progress = null;
  }

}
